import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs/Observable';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { environment } from '../../environments/environment';
import { VocTerms } from '../models/voc-term';
import { RemoveBracketsContentPipe } from '../pipes/remove-brackets-content.pipe';

const EMPTY_TERM_REGEX = /^[‒-]$/u;

@Injectable()
export class VocsService {
    private httpHeaders: HttpHeaders;
    private httpParams: HttpParams;
    private msTypes$: BehaviorSubject<VocTerms>;
    private countries$: BehaviorSubject<VocTerms>;
    private cities$: BehaviorSubject<VocTerms>;
    private libraries$: BehaviorSubject<VocTerms>;
    private msCopyPlaces$: BehaviorSubject<VocTerms>;
    private sifaSections$: BehaviorSubject<VocTerms>;
    private sifaParts$: BehaviorSubject<VocTerms>;
    private sifaTreatises$: BehaviorSubject<VocTerms>;
    private sifaChapters$: BehaviorSubject<VocTerms>;
    private sifaOtherElements$: BehaviorSubject<VocTerms>;
    private biblioTypes$: BehaviorSubject<VocTerms>;
    private biblioRespTypes$: BehaviorSubject<VocTerms>;
    private remarkPassTypes$: BehaviorSubject<VocTerms>;
    private annotationTypes$: BehaviorSubject<VocTerms>;
    private noteTypes$: BehaviorSubject<VocTerms>;
    private completeness$: BehaviorSubject<VocTerms>;
    private writingHands$: BehaviorSubject<VocTerms>;
    private inks$: BehaviorSubject<VocTerms>;

    constructor(
        private http: HttpClient,
        public removeBracketsContentPipe: RemoveBracketsContentPipe
        ) {
        this.init();
    }

    init(): void {
        this.httpHeaders = new HttpHeaders({
            'Content-Type': 'application/json',
            'Authorization': environment.apiKey,
            'X-PhiBor-App': environment.phiborApp
        });
        this.httpParams = new HttpParams();
        this.httpParams = this.httpParams.set('fields', 'pkid,term,sorting,version');
        this.httpParams = this.httpParams.set('sort', 'term_sort,sorting');

        /* Load all vocs */
        this.loadMsTypes();
        this.loadCountries();
        this.loadCities();
        this.loadLibraries();
        this.loadMsCopyPlaces();
        this.loadSifaSections();
        this.loadSifaParts();
        this.loadSifaTreatises();
        this.loadSifaChapters();
        this.loadSifaOtherElements();
        this.loadBiblioTypes();
        this.loadBiblioRespTypes();
        this.loadRemarkPassTypes();
        this.loadAnnotationTypes();
        this.loadNoteTypes();
        this.loadCompleteness();
        this.loadWritingHands();
        this.loadInks();
    }

    /* Load vocabulary of manuscript types */
    private loadMsTypes(): void {
        this.msTypes$ = new BehaviorSubject<VocTerms>([]);
        this.http.get<VocTerms>(
            environment.endpoints.de + '/vocs/ms_types',
            {
                headers: this.httpHeaders,
                params: this.httpParams.set('expand', '__countMss')
            }
        )
            .subscribe(
                response => this.msTypes$.next(
                    response.filter(voc => voc.__countMss > 0)
                ),
                error => this.msTypes$.error(error)
            );
    }

    /* Load vocabulary of countries */
    private loadCountries(): void {
        this.countries$ = new BehaviorSubject<VocTerms>([]);
        this.http.get<VocTerms>(
            environment.endpoints.de + '/vocs/countries',
            {
                headers: this.httpHeaders,
                params: this.httpParams.set('expand', '__countMss')
            }
        )
            .subscribe(
                response => this.countries$.next(
                    response.filter(voc => !voc.term.match(EMPTY_TERM_REGEX) && voc.__countMss > 0)
                ),
                error => this.countries$.error(error)
            );
    }

    /* Load vocabulary of cities */
    private loadCities(): void {
        this.cities$ = new BehaviorSubject<VocTerms>([]);
        this.http.get<VocTerms>(
            environment.endpoints.de + '/vocs/cities',
            {
                headers: this.httpHeaders,
                params: this.httpParams.set('expand', '__countMss')
            }
        )
            .subscribe(
                response => this.cities$.next(
                    response.filter(voc => !voc.term.match(EMPTY_TERM_REGEX) && voc.__countMss > 0)
                ),
                error => this.cities$.error(error)
            );
    }

    /* Load vocabulary of libraries */
    private loadLibraries(): void {
        this.libraries$ = new BehaviorSubject<VocTerms>([]);
        this.http.get<VocTerms>(
            environment.endpoints.de + '/vocs/libraries',
            {
                headers: this.httpHeaders,
                params: this.httpParams
                    .set('fields', this.httpParams.get('fields') + ',latitude,longitude,fkidcertainty,fkidcities')
                    .set('expand', '__countMss')
            }
        )
            .subscribe(
                response => this.libraries$.next(
                    response.filter(voc => !voc.term.match(EMPTY_TERM_REGEX) && voc.__countMss > 0)
                ),
                error => this.libraries$.error(error)
            );
    }

    /* Load all linked (by manuscripts) copy places */
    private loadMsCopyPlaces(): void {
        this.msCopyPlaces$ = new BehaviorSubject<VocTerms>([]);
        this.http.get<VocTerms>(
            environment.endpoints.de + '/copy-places',
            {
                headers: this.httpHeaders,
                params: this.httpParams
                    .set('fields', this.httpParams.get('fields') + ',latitude,longitude,fkidcertainty')
                    .set('expand', '__countMss')
            }
        )
            .subscribe(
                response => this.msCopyPlaces$.next(
                    response.filter(voc => voc.__countMss > 0)
                ),
                error => this.msCopyPlaces$.error(error)
            );
    }

    /* Load Šifāʾ reference sections */
    private loadSifaSections(): void {
        this.sifaSections$ = new BehaviorSubject<VocTerms>([]);
        this.http.get<VocTerms>(
            environment.endpoints.de + '/vocs/gridstruct_sections',
            {
                headers: this.httpHeaders,
                params: this.httpParams
                    .set('fields', this.httpParams.get('fields') + ',term_arabic')
                    .set('sort', 'sorting,term_sort')
            }
        )
            .subscribe(
                response => this.sifaSections$.next(response),
                error => this.sifaSections$.error(error)
            );
    }

    /* Load Šifāʾ reference parts */
    private loadSifaParts(): void {
        this.sifaParts$ = new BehaviorSubject<VocTerms>([]);
        this.http.get<VocTerms>(
            environment.endpoints.de + '/vocs/gridstruct_parts',
            {
                headers: this.httpHeaders,
                params: this.httpParams
                    .set('fields', this.httpParams.get('fields') + ',fkidgridstruct_sections')
                    .set('sort', 'sorting,term_sort')
            }
        )
            .subscribe(
                response => this.sifaParts$.next(response),
                error => this.sifaParts$.error(error)
            );
    }

    /* Load Šifāʾ reference treatises */
    private loadSifaTreatises(): void {
        this.sifaTreatises$ = new BehaviorSubject<VocTerms>([]);
        this.http.get<VocTerms>(
            environment.endpoints.de + '/vocs/gridstruct_treatises',
            {
                headers: this.httpHeaders,
                params: this.httpParams
                    .set('fields', this.httpParams.get('fields') + ',fkidgridstruct_parts')
                    .set('sort', 'sorting,term_sort')
            }
        )
            .subscribe(
                response => this.sifaTreatises$.next(response),
                error => this.sifaTreatises$.error(error)
            );
    }

    /* Load Šifāʾ reference chapters */
    private loadSifaChapters(): void {
        this.sifaChapters$ = new BehaviorSubject<VocTerms>([]);
        this.http.get<VocTerms>(
            environment.endpoints.de + '/vocs/gridstruct_chapters',
            {
                headers: this.httpHeaders,
                params: this.httpParams
                    .set('fields', this.httpParams.get('fields') + ',fkidgridstruct_treatises')
                    .set('sort', 'sorting,term_sort')
            }
        )
            .subscribe(
                response => this.sifaChapters$.next(response),
                error => this.sifaChapters$.error(error)
            );
    }

    /* Load Šifāʾ reference other manuscript elements */
    private loadSifaOtherElements(): void {
        this.sifaOtherElements$ = new BehaviorSubject<VocTerms>([]);
        this.http.get<VocTerms>(
            environment.endpoints.de + '/vocs/ms_structure',
            {
                headers: this.httpHeaders,
                params: this.httpParams
                    .set('fields', this.httpParams.get('fields') + ',term_norm')
                    .set('sort', 'term_sort,sorting')
            }
        )
            .subscribe(
                response => this.sifaOtherElements$.next(response),
                error => this.sifaOtherElements$.error(error)
            );
    }

    /* Load vocabulary of bibliographic types */
    private loadBiblioTypes(): void {
        this.biblioTypes$ = new BehaviorSubject<VocTerms>([]);
        this.http.get<VocTerms>(
            environment.endpoints.de + '/vocs/biblio_types',
            {headers: this.httpHeaders, params: this.httpParams}
        )
            .subscribe(
                response => this.biblioTypes$.next(response),
                error => this.biblioTypes$.error(error)
            );
    }

    /* Load vocabulary of bibliographic responsability types */
    private loadBiblioRespTypes(): void {
        this.biblioRespTypes$ = new BehaviorSubject<VocTerms>([]);
        this.http.get<VocTerms>(
            environment.endpoints.de + '/vocs/biblio_resp_types',
            {headers: this.httpHeaders, params: this.httpParams}
        )
            .subscribe(
                response => this.biblioRespTypes$.next(response),
                error => this.biblioRespTypes$.error(error)
            );
    }

    /* Load vocabulary of manuscript remarkable passage types */
    private loadRemarkPassTypes(): void {
        const __this = this;
        this.remarkPassTypes$ = new BehaviorSubject<VocTerms>([]);
        this.http.get<VocTerms>(
            environment.endpoints.de + '/vocs/remarkable_passages',
            {headers: this.httpHeaders, params: this.httpParams}
        )
            .subscribe(
                response => this.remarkPassTypes$.next(response.map(
                    function (voc) {
                        voc.term = __this.removeBracketsContentPipe.transform(voc.term);
                        voc.term_norm = __this.removeBracketsContentPipe.transform(voc.term_norm);
                        return voc;
                    }
                )),
                error => this.remarkPassTypes$.error(error)
            );
    }

    /* Load vocabulary of image annotation types */
    private loadAnnotationTypes(): void {
        const __this = this;
        this.annotationTypes$ = new BehaviorSubject<VocTerms>([]);
        this.http.get<VocTerms>(
            environment.endpoints.de + '/vocs/annotation_types',
            {headers: this.httpHeaders, params: this.httpParams}
        )
            .subscribe(
                response => this.annotationTypes$.next(response.map(
                    function (voc) {
                        voc.term = __this.removeBracketsContentPipe.transform(voc.term);
                        voc.term_norm = __this.removeBracketsContentPipe.transform(voc.term_norm);
                        return voc;
                    }
                )),
                error => this.annotationTypes$.error(error)
            );
    }

    /* Load vocabulary of note types */
    private loadNoteTypes(): void {
        const __this = this;
        this.noteTypes$ = new BehaviorSubject<VocTerms>([]);
        this.http.get<VocTerms>(
            environment.endpoints.de + '/vocs/note_types',
            {headers: this.httpHeaders, params: this.httpParams}
        )
            .subscribe(
                response => this.noteTypes$.next(response.map(
                    function (voc) {
                        voc.term = __this.removeBracketsContentPipe.transform(voc.term);
                        voc.term_norm = __this.removeBracketsContentPipe.transform(voc.term_norm);
                        return voc;
                    }
                )),
                error => this.noteTypes$.error(error)
            );
    }

    /* Load vocabulary of completeness states */
    private loadCompleteness(): void {
        this.completeness$ = new BehaviorSubject<VocTerms>([]);
        this.http.get<VocTerms>(
            environment.endpoints.de + '/vocs/completeness',
            {headers: this.httpHeaders, params: this.httpParams}
        )
            .subscribe(
                response => this.completeness$.next(response),
                error => this.completeness$.error(error)
            );
    }

    /* Load vocabulary of writing hands */
    private loadWritingHands(): void {
        this.writingHands$ = new BehaviorSubject<VocTerms>([]);
        this.http.get<VocTerms>(
            environment.endpoints.de + '/vocs/writing_hands',
            {headers: this.httpHeaders, params: this.httpParams}
        )
            .subscribe(
                response => this.writingHands$.next(response),
                error => this.writingHands$.error(error)
            );
    }

    /* Load vocabulary of inks */
    private loadInks(): void {
        this.inks$ = new BehaviorSubject<VocTerms>([]);
        this.http.get<VocTerms>(
            environment.endpoints.de + '/vocs/inks',
            {headers: this.httpHeaders, params: this.httpParams}
        )
            .subscribe(
                response => this.inks$.next(response),
                error => this.inks$.error(error)
            );
    }

    /* Get vocabulary of manuscript types */
    getMsTypes(): Observable<VocTerms> {
        return this.msTypes$.asObservable();
    }

    /* Get vocabulary of countries */
    getCountries(): Observable<VocTerms> {
        return this.countries$.asObservable();
    }

    /* Get vocabulary of cities */
    getCities(): Observable<VocTerms> {
        return this.cities$.asObservable();
    }

    /* Get vocabulary of libraries */
    getLibraries(): Observable<VocTerms> {
        return this.libraries$.asObservable();
    }

    /* Get all linked (by manuscripts) copy places */
    getMsCopyPlaces(): Observable<VocTerms> {
        return this.msCopyPlaces$.asObservable();
    }

    /* Get Šifāʾ reference sections */
    getSifaSections(): Observable<VocTerms> {
        return this.sifaSections$.asObservable();
    }

    /* Get Šifāʾ reference parts */
    getSifaParts(): Observable<VocTerms> {
        return this.sifaParts$.asObservable();
    }

    /* Get Šifāʾ reference treatises */
    getSifaTreatises(): Observable<VocTerms> {
        return this.sifaTreatises$.asObservable();
    }

    /* Get Šifāʾ reference chapters */
    getSifaChapters(): Observable<VocTerms> {
        return this.sifaChapters$.asObservable();
    }

    /* Get Šifāʾ reference other manuscript elements */
    getSifaOtherElements(): Observable<VocTerms> {
        return this.sifaOtherElements$.asObservable();
    }

    /* Get vocabulary of bibliographic types */
    getBiblioTypes(): Observable<VocTerms> {
        return this.biblioTypes$.asObservable();
    }

    /* Get vocabulary of bibliographic responsability types */
    getBiblioRespTypes(): Observable<VocTerms> {
        return this.biblioRespTypes$.asObservable();
    }

    /* Get vocabulary of manuscript remarkable passage types */
    getRemarkPassTypes(): Observable<VocTerms> {
        return this.remarkPassTypes$.asObservable();
    }

    /* Get vocabulary of image annotation types */
    getAnnotationTypes(): Observable<VocTerms> {
        return this.annotationTypes$.asObservable();
    }

    /* Get vocabulary of note types */
    getNoteTypes(): Observable<VocTerms> {
        return this.noteTypes$.asObservable();
    }

    /* Get vocabulary of completeness states */
    getCompleteness(): Observable<VocTerms> {
        return this.completeness$.asObservable();
    }

    /* Get vocabulary of writing hands */
    getWritingHands(): Observable<VocTerms> {
        return this.writingHands$.asObservable();
    }

    /* Get vocabulary of inks */
    getInks(): Observable<VocTerms> {
        return this.inks$.asObservable();
    }
}



// WEBPACK FOOTER //
// ./src/app/services/vocs.service.ts